import React, { useEffect, useState } from "react";
import "./SalesReport.css";

const INVOICE_API = "/api/invoices";

export default function TaxReport({ fromDate, toDate }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fromDate && toDate) loadInvoices();
  }, [fromDate, toDate]);

  async function loadInvoices() {
    setLoading(true);
    try {
      const res = await fetch(
        `${INVOICE_API}?fromDate=${fromDate}&toDate=${toDate}`
      );
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // Build one row per invoice
  const rows = invoices.map(inv => {
    const items = inv.deliveries?.flatMap(d => d.items || []) || [];

    const taxableAmt = items.reduce((s, it) =>
      s + Number(it.qty || 0) * Number(it.rate || 0), 0);

    const totalTax = items.reduce((s, it) => {
      const qty  = Number(it.qty  || 0);
      const rate = Number(it.rate || 0);
      const tp   = Number(it.taxPercent || 0);
      return s + qty * rate * tp / 100;
    }, 0);

    const taxPercent  = Number(items[0]?.taxPercent || 0);
    const cgstPercent = taxPercent / 2;
    const sgstPercent = taxPercent / 2;
    const cgstAmt     = totalTax / 2;
    const sgstAmt     = totalTax / 2;
    const billAmt     = taxableAmt + totalTax;

    return {
      invoiceNo: inv.invoiceNo,
      date: inv.invoiceDate,
      partyName: inv.partyName,
      taxableAmt,
      cgstPercent,
      cgstAmt,
      sgstPercent,
      sgstAmt,
      totalTax,
      billAmt
    };
  });

  // Totals
  const totTaxable = rows.reduce((s, r) => s + r.taxableAmt, 0);
  const totCgst    = rows.reduce((s, r) => s + r.cgstAmt,    0);
  const totSgst    = rows.reduce((s, r) => s + r.sgstAmt,    0);
  const totTax     = rows.reduce((s, r) => s + r.totalTax,   0);
  const totBill    = rows.reduce((s, r) => s + r.billAmt,    0);

  // ── PRINT — exactly same style as Sales Report ──
  function handlePrint() {
    if (!rows.length) return alert("No data to print");

    const win = window.open("", "_blank");
    win.document.write(`
      <html>
      <head>
        <title>Tax Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .report-wrapper { border: 2px solid #000; padding: 10px; }
          .header-section { position: relative; margin-bottom: 15px; }
          .company-section { text-align: center; }
          .title { font-size: 20px; font-weight: bold; }
          .sub-title { margin-top: 4px; font-size: 14px; }
          .date-section {
            position: absolute; right: 0; top: 0;
            text-align: right; font-weight: bold; font-size: 14px;
          }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 6px; font-size: 12px; text-align: center; }
          th { background: #eaeaea; }
          td:nth-child(3) { text-align: left; }
          tfoot td { font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="report-wrapper">

          <div class="header-section">
            <div class="company-section">
              <div class="title">ORANGE TRADINGS</div>
              <div class="sub-title">DHARAPURAM ROAD, TIRUPUR - 641 601</div>
            </div>
            <div class="date-section">
              <div>From Date : ${fromDate}</div>
              <div>To Date&nbsp;&nbsp;&nbsp;: ${toDate}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Inv No</th>
                <th>Date</th>
                <th>Party Name</th>
                <th>Taxable Amt</th>
                <th>CGST %</th>
                <th>CGST Amt</th>
                <th>SGST %</th>
                <th>SGST Amt</th>
                <th>Total Tax</th>
                <th>Bill Amt</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => `
                <tr>
                  <td>${r.invoiceNo}</td>
                  <td>${r.date}</td>
                  <td style="text-align:left">${r.partyName}</td>
                  <td>${r.taxableAmt.toFixed(2)}</td>
                  <td>${r.cgstPercent.toFixed(1)}%</td>
                  <td>${r.cgstAmt.toFixed(2)}</td>
                  <td>${r.sgstPercent.toFixed(1)}%</td>
                  <td>${r.sgstAmt.toFixed(2)}</td>
                  <td>${r.totalTax.toFixed(2)}</td>
                  <td>${r.billAmt.toFixed(2)}</td>
                </tr>
              `).join("")}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3">Total</td>
                <td>${totTaxable.toFixed(2)}</td>
                <td></td>
                <td>${totCgst.toFixed(2)}</td>
                <td></td>
                <td>${totSgst.toFixed(2)}</td>
                <td>${totTax.toFixed(2)}</td>
                <td>${totBill.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

        </div>
      </body>
      </html>
    `);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); win.close(); };
  }

  function handleClear() {
    setInvoices([]);
  }

 return (
  <div className="report-container">
    <div className="report-card" style={{ paddingTop: 8, paddingBottom: 8 }}>

      <h2 className="report-title" style={{ marginBottom: 6, marginTop: 0 }}>
        TAX REPORT
      </h2>

      {loading && <div style={{ padding: 20 }}>Loading...</div>}

      {!loading && (
        <>
          {/* ── SCROLLABLE TABLE ── */}
          <div style={{
            maxHeight: "calc(100vh - 380px)",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #ccc",
            marginBottom: 8
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ position: "sticky", top: 0, background: "#b9a8d3", zIndex: 1 }}>
                  <th style={th}>Inv No</th>
                  <th style={th}>Date</th>
                  <th style={{ ...th, textAlign: "left" }}>Party Name</th>
                  <th style={th}>Taxable Amt</th>
                  <th style={th}>CGST %</th>
                  <th style={th}>CGST Amt</th>
                  <th style={th}>SGST %</th>
                  <th style={th}>SGST Amt</th>
                  <th style={th}>Total Tax</th>
                  <th style={th}>Bill Amt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f4f4f4" }}>
                    <td style={td}>{r.invoiceNo}</td>
                    <td style={td}>{r.date}</td>
                    <td style={{ ...td, textAlign: "left" }}>{r.partyName}</td>
                    <td style={tdR}>{r.taxableAmt.toFixed(2)}</td>
                    <td style={td}>{r.cgstPercent.toFixed(1)}%</td>
                    <td style={tdR}>{r.cgstAmt.toFixed(2)}</td>
                    <td style={td}>{r.sgstPercent.toFixed(1)}%</td>
                    <td style={tdR}>{r.sgstAmt.toFixed(2)}</td>
                    <td style={tdR}>{r.totalTax.toFixed(2)}</td>
                    <td style={tdR}>{r.billAmt.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── SUMMARY TOTALS — same as Stock Movement ── */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
            gap: 8,
            marginBottom: 10,
            padding: "6px 4px"
          }}>
            <div style={summaryBox}>
              <div style={summaryLabel}>Total Taxable</div>
              <input style={summaryInput} value={totTaxable.toFixed(2)} readOnly />
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Total CGST</div>
              <input style={summaryInput} value={totCgst.toFixed(2)} readOnly />
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Total SGST</div>
              <input style={summaryInput} value={totSgst.toFixed(2)} readOnly />
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Total Tax</div>
              <input style={summaryInput} value={totTax.toFixed(2)} readOnly />
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Total Bill Amt</div>
              <input style={{ ...summaryInput, background: "#d8f5e6", border: "1px solid #16a34a", fontWeight: 700 }}
                value={totBill.toFixed(2)} readOnly />
            </div>
          </div>

          {/* ── PRINT BUTTON ── */}
          <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
            <button className="report-print-btn" onClick={handlePrint}>
              Print
            </button>
            <button className="report-clear-btn" onClick={handleClear}>
              Clear
            </button>
          </div>
        </>
      )}

    </div>
  </div>
);
}

// Inline styles for table cells
const th = {
  border: "1px solid #000",
  padding: "6px 8px",
  fontWeight: 700,
  textAlign: "center",
  fontSize: 12
};

const td = {
  border: "1px solid #ccc",
  padding: "5px 8px",
  textAlign: "center",
  fontSize: 12
};

const tdR = {
  border: "1px solid #ccc",
  padding: "5px 8px",
  textAlign: "right",
  fontSize: 12
};
const summaryBox = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center"
};

const summaryLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#555",
  marginBottom: 2
};

const summaryInput = {
  width: "100%",
  padding: "4px 6px",
  textAlign: "right",
  borderRadius: 4,
  border: "1px solid #ccc",
  background: "#f4f3ff",
  fontSize: 13,
  fontWeight: 600
};